import React, { useRef, useState, useEffect, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { MapPin, Check } from "lucide-react";
import "./AddressField.css";

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

interface AddressFieldProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  validation: { isValid: boolean; error: string; touched: boolean };
  onBlur: () => void;
}

declare global {
  interface Window {
    google: typeof google;
  }
}

declare namespace google {
  namespace maps {
    interface MapsEventListener {
      remove(): void;
    }

    namespace places {
      interface AutocompleteOptions {
        types?: string[];
        componentRestrictions?: { country: string };
      }

      interface PlaceResult {
        formatted_address?: string;
        geometry?: {
          location?: {
            lat(): number;
            lng(): number;
          };
        };
      }

      class Autocomplete {
        constructor(inputField: HTMLInputElement, opts?: AutocompleteOptions);
        getPlace(): PlaceResult;
        addListener(eventName: string, handler: Function): MapsEventListener;
      }
    }
  }
}

// Custom hook to load external scripts
const useScript = (src: string): boolean => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // If script is already loaded
    if (window.google?.maps?.places) {
      setLoaded(true);
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      setLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => setLoaded(true);
    script.onerror = (error) => {
      console.error("Error loading script:", error);
      setLoaded(false);
    };

    document.head.appendChild(script);

    return () => {
      // Only remove if we added it
      if (!existingScript) {
        const scriptToRemove = document.querySelector(`script[src="${src}"]`);
        if (scriptToRemove && scriptToRemove.parentNode) {
          scriptToRemove.parentNode.removeChild(scriptToRemove);
        }
      }
    };
  }, [src]);

  return loaded;
};

export const AddressField = ({ value, onChange, label, validation, onBlur }: AddressFieldProps) => {
  const [showMap, setShowMap] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [mapUrl, setMapUrl] = useState("");
  const listenerRef = useRef<google.maps.MapsEventListener | null>(null);

  const isScriptLoaded = useScript(
    `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`
  );

  const handlePlaceChanged = useCallback(
    (autocomplete: google.maps.places.Autocomplete) => {
      const place = autocomplete.getPlace();
      if (!place.formatted_address) return;

      onChange(place.formatted_address);
      if (place.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMapUrl(`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${lat},${lng}&zoom=17`);
        setShowMap(true);
      }
    },
    [onChange]
  );

  useEffect(() => {
    if (!isScriptLoaded || !inputRef.current) return;

    const initAutocomplete = () => {
      try {
        if (!window.google?.maps?.places?.Autocomplete) {
          const retryDelay = 1000;
          
          setTimeout(initAutocomplete, retryDelay);
          return;
        }

        const autocompleteInstance = new window.google.maps.places.Autocomplete(inputRef.current!, {
          types: ["address"],
          componentRestrictions: { country: "PT" },
        });

        // Store listener reference for cleanup
        listenerRef.current = autocompleteInstance.addListener("place_changed", () =>
          handlePlaceChanged(autocompleteInstance)
        );
      } catch (error) {
        console.error("Error initializing autocomplete:", error);
      }
    };

    initAutocomplete();

    return () => {
      // Clean up listener
      if (listenerRef.current) {
        listenerRef.current.remove();
        listenerRef.current = null;
      }
    };
  }, [isScriptLoaded, handlePlaceChanged]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (!e.target.value.trim()) {
      setShowMap(false);
      setMapUrl("");
    }
  };

  const handleAnimationStart = (e: React.AnimationEvent) => {
    if (e.animationName === "onAutoFillStart" && inputRef.current) {
      const event = new Event("input", { bubbles: true });
      inputRef.current.dispatchEvent(event);
    }
  };

  return (
    <div className="space-y-2.5 md:col-span-2">
      <Label htmlFor="address" className="text-base font-medium text-gray-700">
        {label} {label && "*"}
      </Label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          id="address"
          name="address"
          value={value}
          onChange={handleInputChange}
          onAnimationStart={handleAnimationStart}
          onBlur={onBlur}
          className={`w-full h-12 pl-10 pr-4 text-base transition-all duration-200 focus:ring-2 rounded-md border placeholder:text-gray-300 ${
            validation.touched && validation.error
              ? "border-red-300 focus:ring-red-500/20 hover:border-red-400"
              : validation.touched && validation.isValid
              ? "border-green-300 focus:ring-green-500/20 hover:border-green-400"
              : "border-gray-200 focus:ring-teal-500/20 hover:border-gray-300"
          } address-autofill-animation`}
          placeholder={label}
          aria-label={label}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded="false"
        />
        {validation.touched && validation.isValid && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Check className="h-5 w-5 text-green-500" />
          </div>
        )}
      </div>
      {validation.touched && validation.error && <p className="text-sm text-red-500">{validation.error}</p>}
      {showMap && mapUrl && (
        <div className="mt-4 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
          <iframe
            width="100%"
            height="200"
            style={{ border: 0 }}
            loading="lazy"
            src={mapUrl}
            allowFullScreen
            title="Location map"
            aria-label="Location map"
          />
        </div>
      )}
    </div>
  );
};
